<app-base-modal
  [open]="isOpen()"
  (openChange)="onOpenChange($event)"
  [title]="readonly ? 'Paper details' : 'Edit paper metadata'"
  [size]="'md'"
>
  <div class="flex flex-col gap-3" data-testid="edit-metadata-dialog">
    @if (otherProjectsCount() > 0) {
      <div
        class="rounded-md bg-blue-50 px-3 py-2 text-xs text-blue-800"
        data-testid="edit-metadata-shared-notice"
      >
        ⓘ This paper is also in {{ otherProjectsCount() }} other project(s) in this group. Edits
        apply to all of them.
      </div>
    }

    <form [formGroup]="form" (ngSubmit)="save()" class="flex flex-1 flex-col gap-3 overflow-y-auto">
      <div class="flex flex-col gap-1">
        <label class="input-label" for="edit-title"
          >Title <span class="text-red-600">*</span></label
        >
        <input
          id="edit-title"
          type="text"
          class="input-field"
          formControlName="title"
          required
          data-testid="edit-metadata-title"
        />
        @if (form.controls.title.touched && form.controls.title.invalid) {
          <p class="text-xs text-red-700">Title is required.</p>
        }
      </div>

      <div class="flex flex-col gap-1">
        <label class="input-label" for="edit-authors"
          >Authors
          @if (!form.controls.authorsCsv.value?.trim()) {
            <span class="text-xs text-amber-600">(needed for screening)</span>
          }
        </label>
        <input
          id="edit-authors"
          type="text"
          class="input-field"
          formControlName="authorsCsv"
          placeholder="Alice, Bob, Carol"
          data-testid="edit-metadata-authors"
        />
        <p class="text-xs text-gray-500">Comma-separated.</p>
      </div>

      <div class="flex flex-col gap-3 sm:flex-row">
        <div class="flex flex-1 flex-col gap-1">
          <label class="input-label" for="edit-year">Year</label>
          <input
            id="edit-year"
            type="number"
            class="input-field"
            formControlName="year"
            [min]="1000"
            [max]="maxYear"
            data-testid="edit-metadata-year"
          />
          @if (yearWarning(); as msg) {
            <p class="text-xs text-red-700" data-testid="edit-metadata-year-warning">{{ msg }}</p>
          }
        </div>
        <div class="flex flex-1 flex-col gap-1">
          <label class="input-label" for="edit-venue">Venue</label>
          <input
            id="edit-venue"
            type="text"
            class="input-field"
            formControlName="venue"
            data-testid="edit-metadata-venue"
          />
        </div>
      </div>

      <div class="flex flex-col gap-1">
        <label class="input-label" for="edit-doi">DOI</label>
        @if (doiUrl(); as url) {
          <a
            [href]="url"
            target="_blank"
            rel="noopener noreferrer"
            class="text-xs text-blue-600 hover:text-blue-800 hover:underline mb-1 break-all"
          >
            🔗 {{ url }}
          </a>
        }
        <input
          id="edit-doi"
          type="text"
          class="input-field"
          formControlName="doi"
          placeholder="10.1000/xyz"
          data-testid="edit-metadata-doi"
        />
        @if (doiWarning()) {
          <p class="text-xs text-amber-700" data-testid="edit-metadata-doi-warning">
            That doesn't look like a DOI (expected 10.xxxx/...). You can still save.
          </p>
        }
      </div>

      @if (externalIdIsUrl(); as url) {
        <div class="flex flex-col gap-1" data-testid="edit-metadata-external-id">
          <label class="input-label">External ID</label>
          <a
            [href]="url"
            target="_blank"
            rel="noopener noreferrer"
            class="text-xs text-blue-600 hover:text-blue-800 hover:underline break-all"
          >
            {{ url }}
          </a>
        </div>
      }

      <div class="flex flex-col gap-1">
        <label class="input-label" for="edit-abstract"
          >Abstract
          @if (!form.controls.abstractText.value?.trim()) {
            <span class="text-xs text-amber-600">(needed for screening)</span>
          }
        </label>
        <textarea
          id="edit-abstract"
          class="input-field min-h-[6rem]"
          formControlName="abstractText"
          rows="4"
          data-testid="edit-metadata-abstract"
        ></textarea>
      </div>

      <div class="flex flex-col gap-3 sm:flex-row">
        <div class="flex flex-1 flex-col gap-1">
          <label class="input-label" for="edit-work-type">Work type</label>
          <select
            id="edit-work-type"
            class="select-field"
            formControlName="workType"
            data-testid="edit-metadata-work-type"
          >
            <option value="">Unknown</option>
            @for (opt of workTypeOptions; track opt.value) {
              <option [value]="opt.value">{{ opt.label }}</option>
            }
          </select>
        </div>
        <div class="flex flex-1 flex-col gap-1">
          <label class="input-label" for="edit-source-type">Venue type</label>
          <select
            id="edit-source-type"
            class="select-field"
            formControlName="sourceType"
            data-testid="edit-metadata-source-type"
          >
            <option value="">Unknown</option>
            @for (opt of sourceTypeOptions; track opt.value) {
              <option [value]="opt.value">{{ opt.label }}</option>
            }
          </select>
        </div>
      </div>

      @if (!readonly && submitError(); as msg) {
        <app-base-alert
          type="error"
          title="Could not save"
          [message]="msg"
          [closeable]="false"
          data-testid="edit-metadata-error"
        ></app-base-alert>
      }

      @if (readonly) {
        <div class="flex justify-end">
          <button
            type="button"
            (click)="close()"
            class="btn-primary"
            data-testid="edit-metadata-close"
          >
            Close
          </button>
        </div>
      } @else {
        <div class="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            (click)="close()"
            class="btn-small-secondary"
            data-testid="edit-metadata-cancel"
          >
            Cancel
          </button>
          <button
            type="submit"
            class="btn-primary"
            [disabled]="form.invalid || submitting()"
            data-testid="edit-metadata-save"
          >
            {{ submitting() ? 'Saving…' : 'Save' }}
          </button>
        </div>
      }
    </form>
  </div>
</app-base-modal>
